import apiClient from '../client';

export interface ConversationTag {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  merged_into_tag_id: string | null;
  is_active: boolean;
  assignment_count?: number;
}

export interface CreateTagRequest {
  name: string;
  description?: string;
}

export interface UpdateTagRequest {
  name?: string;
  description?: string;
}

export interface MergeTagRequest {
  target_tag_id: string;
}

export interface ReassignTagRequest {
  conversation_ids: string[];
  action: 'add' | 'remove' | 'replace';
}

export const tagsService = {
  /**
   * Get all tags
   */
  getTags: async (activeOnly: boolean = false): Promise<ConversationTag[]> => {
    const result = await apiClient.get<ConversationTag[]>('/admin/tags', {
      params: { active_only: activeOnly },
    });
    return result as unknown as ConversationTag[];
  },

  /**
   * Get tag by ID
   */
  getTagById: async (id: string): Promise<ConversationTag> => {
    const tags = await tagsService.getTags(false);
    const tag = tags.find(t => t.id === id);
    if (!tag) {
      throw new Error(`Tag not found: ${id}`);
    }
    return tag;
  },

  /**
   * Create a new tag
   */
  createTag: async (data: CreateTagRequest): Promise<ConversationTag> => {
    const result = await apiClient.post<ConversationTag>('/admin/tags', data);
    return result as unknown as ConversationTag;
  },

  /**
   * Update a tag
   */
  updateTag: async (id: string, data: UpdateTagRequest): Promise<ConversationTag> => {
    const result = await apiClient.put<ConversationTag>(`/admin/tags/${id}`, data);
    return result as unknown as ConversationTag;
  },

  /**
   * Delete a tag
   */
  deleteTag: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/tags/${id}`);
  },

  /**
   * Merge a tag into another tag
   */
  mergeTag: async (sourceTagId: string, targetTagId: string): Promise<void> => {
    await apiClient.post(`/admin/tags/${sourceTagId}/merge`, {
      target_tag_id: targetTagId,
    });
  },

  /**
   * Reassign tag to conversations
   */
  reassignTag: async (
    tagId: string,
    conversationIds: string[],
    action: 'add' | 'remove' | 'replace'
  ): Promise<void> => {
    await apiClient.post(`/admin/tags/${tagId}/reassign`, {
      conversation_ids: conversationIds,
      action,
    });
  },

  /**
   * Get conversations with a tag
   */
  getConversationsWithTag: async (tagId: string): Promise<string[]> => {
    const result = await apiClient.get<string[]>(`/admin/tags/${tagId}/conversations`);
    return result as unknown as string[];
  },
};

